import React, { useCallback, useEffect, useState } from 'react';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

/**
 * Short-lived support access to one account.
 *
 * Drop-in for any account screen — customer, staff, rider, fleet — since the workflow
 * is the same wherever the account lives. Renders nothing at all for staff without
 * can_generate_temp_password, so it is safe to place unconditionally.
 *
 * The generated password is shown ONCE. It is hashed server-side and there is no way
 * to retrieve it again, so the UI has to make copying it the obvious next action.
 */

type Props = {
    userId: number | string;
    email?: string;
    name?: string;
};

const when = (v: any) => (v ? new Date(v).toLocaleString() : '—');

const STATUS_STYLES: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    used: 'bg-gray-200 text-gray-600',
    expired: 'bg-amber-100 text-amber-700',
    revoked: 'bg-red-100 text-red-700',
};

function TemporaryPasswordControl({ userId, email, name }: Props) {
    const userLoginData: any = useSelector((state: any) => state.data.login.value);

    // Matches ProtectedRoute and AdminSidebar: the logged-in user hangs off
    // `.data`, and `permissions` is a JSON *string*, not an array. Reading
    // userLoginData.permissions directly returns undefined and hides this panel
    // from everyone, which is exactly what happened.
    const canGenerate = React.useMemo(() => {
        const role = userLoginData?.data?.role;

        if (role === 'admin') return true;

        try {
            const list: string[] = userLoginData?.data?.permissions
                ? JSON.parse(userLoginData.data.permissions)
                : [];
            return Array.isArray(list) && list.includes('can_generate_temp_password');
        } catch {
            return false;
        }
    }, [userLoginData]);

    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [issued, setIssued] = useState<any>(null);
    const [live, setLive] = useState<any>(null);
    const [history, setHistory] = useState<any[]>([]);
    const [copied, setCopied] = useState(false);

    const loadHistory = useCallback(async () => {
        if (!canGenerate || !userId) return;
        try {
            const res = await SuperAdminApis.getTemporaryPasswordHistory(userId);
            if (res?.success) {
                setLive(res.data.live ?? null);
                setHistory(res.data.history ?? []);
            }
        } catch {
            // Non-fatal: the button still works without the history panel.
        }
    }, [canGenerate, userId]);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    if (!canGenerate) return null;

    const generate = async () => {
        if (reason.trim().length < 5) {
            toast.warn('Say why access is needed — it is recorded against the account');
            return;
        }

        setBusy(true);
        try {
            const res = await SuperAdminApis.generateTemporaryPassword(userId, { reason });
            if (res?.success) {
                setIssued(res.data);
                setReason('');
                setCopied(false);
                loadHistory();
            } else {
                toast.warn(res?.message || 'Could not generate a temporary password');
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not generate a temporary password');
        } finally {
            setBusy(false);
        }
    };

    const revoke = async () => {
        if (!window.confirm('Revoke the live temporary password for this account?')) return;
        setBusy(true);
        try {
            const res = await SuperAdminApis.revokeTemporaryPassword(userId);
            if (res?.success) {
                toast.success(res.message);
                setIssued(null);
                loadHistory();
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not revoke');
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(issued.password);
            setCopied(true);
        } catch {
            // Clipboard is blocked on insecure origins; the password is on screen
            // either way, so say so rather than failing silently.
            toast.info('Copy blocked by the browser — select the password and copy it manually');
        }
    };

    return (
        <div className="bg-white rounded-[10px] p-4 mt-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="text-[15px] font-semibold">Support access</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Generate a one-time password to sign in as {name || email || 'this account'} and
                        help them directly. Expires in 5 hours and can only be used once.
                    </p>
                </div>
                {!open && (
                    <button onClick={() => setOpen(true)}
                        className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm shrink-0">
                        Generate password
                    </button>
                )}
            </div>

            {live && !issued && (
                <div className="mt-3 flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded p-3 flex-wrap">
                    <p className="text-sm text-amber-800">
                        A temporary password is already live on this account, issued by{' '}
                        <strong>{live.generated_by || 'unknown'}</strong> and valid until {when(live.expires_at)}.
                        Generating another will cancel it.
                    </p>
                    <button onClick={revoke} disabled={busy}
                        className="text-sm border border-amber-400 text-amber-800 px-3 py-1.5 rounded disabled:opacity-60">
                        Revoke it
                    </button>
                </div>
            )}

            {open && !issued && (
                <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">
                        Why is access needed? Recorded against the account and shown in the audit log.
                    </label>
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                        placeholder="e.g. Customer called, cannot use the app, booking a shipment on their behalf"
                        className="w-full border rounded-md px-3 py-2 text-sm mb-2" />
                    <div className="flex gap-2">
                        <button onClick={generate} disabled={busy}
                            className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm disabled:opacity-60">
                            {busy ? 'Generating…' : 'Generate'}
                        </button>
                        <button onClick={() => { setOpen(false); setReason(''); }} disabled={busy}
                            className="border px-4 py-2 rounded-md text-sm">Cancel</button>
                    </div>
                </div>
            )}

            {issued && (
                <div className="mt-3 bg-green-50 border border-green-200 rounded p-4">
                    <p className="text-xs text-green-800 mb-2 font-medium">
                        Copy this now — it cannot be shown again.
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-xl font-mono tracking-wider bg-white border rounded px-3 py-2 select-all">
                            {issued.password}
                        </code>
                        <button onClick={copy}
                            className="bg-green-700 text-white px-3 py-2 rounded text-sm">
                            {copied ? 'Copied' : 'Copy'}
                        </button>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                        Sign in at the normal login page with <strong>{issued.account?.email}</strong> and this
                        password. Valid until {when(issued.expires_at)} · single use.
                    </p>
                    <button onClick={() => { setIssued(null); setOpen(false); }}
                        className="text-xs text-gray-500 underline mt-2">Done</button>
                </div>
            )}

            {history.length > 0 && (
                <details className="mt-3">
                    <summary className="text-xs text-gray-500 cursor-pointer">
                        Previous access ({history.length})
                    </summary>
                    <div className="mt-2 overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="text-left text-gray-500">
                                <tr>
                                    <th className="py-1">Issued by</th>
                                    <th className="py-1">Reason</th>
                                    <th className="py-1">Status</th>
                                    <th className="py-1">Used</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((h: any) => (
                                    <tr key={h.id} className="border-t">
                                        <td className="py-1">
                                            {h.generated_by || '—'}
                                            <div className="text-gray-400">{when(h.created_at)}</div>
                                        </td>
                                        <td className="py-1 max-w-[260px]">{h.reason || '—'}</td>
                                        <td className="py-1">
                                            <span className={`px-2 py-0.5 rounded-full ${STATUS_STYLES[h.status] || ''}`}>
                                                {h.status}
                                            </span>
                                        </td>
                                        <td className="py-1">
                                            {h.used_at ? <>{when(h.used_at)}<div className="text-gray-400">{h.used_ip}</div></> : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            )}
        </div>
    );
}

export default TemporaryPasswordControl;
